// 沿用 lingzhi-clipper/src/shared/sse.ts（同一个服务端、同一套协议），只改了 import。
import { LIMITS } from './limits'
import type { Reference } from './reference'

/** 服务端推送的一条事件（只声明本插件用到的字段）。 */
export interface SseEvent {
  response_type?: string
  content?: string
  knowledge_references?: Reference[]
  [key: string]: unknown
}

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null && !Array.isArray(value)

function parseBlock(block: string): SseEvent | null {
  const data = block
    .split('\n')
    .filter((line) => line.startsWith('data:'))
    .map((line) => line.slice(5).trim())
    .join('\n')
  if (!data || data === '[DONE]') return null
  try {
    const value: unknown = JSON.parse(data)
    return isRecord(value) ? (value as SseEvent) : null
  } catch {
    return null // 心跳或损坏的事件直接忽略
  }
}

/** 增量解析器：可以任意切分地喂入文本，事件在空行处才算完整。 */
export function createSseParser() {
  let buffer = ''

  const drain = (final: boolean): SseEvent[] => {
    const parts = buffer.split('\n\n')
    buffer = final ? '' : (parts.pop() ?? '')
    return parts.map(parseBlock).filter((event): event is SseEvent => event !== null)
  }

  return {
    push(chunk: string): SseEvent[] {
      // 先拼再归一化：\r\n 被切在两段之间时也能正确合并。
      buffer = (buffer + chunk).replace(/\r\n/g, '\n')
      return drain(false)
    },
    /** 流结束时调用：把没有结尾空行的最后一块也解析出来。 */
    flush(): SseEvent[] {
      return drain(true)
    },
  }
}

/** 一次性解析完整文本。 */
export function parseEvents(text: string): SseEvent[] {
  const parser = createSseParser()
  return [...parser.push(text), ...parser.flush()]
}

/** 插件关心的事件：思考片段、思考结束、回答片段、引用。其余类型（工具调用、标题、心跳……）都不往下传。 */
export type AskEvent =
  | { kind: 'thinking'; text: string }
  | { kind: 'thinking-done' }
  | { kind: 'answer'; text: string }
  | { kind: 'references'; references: Reference[] }

/** 一条服务端事件对应的插件事件（零到两个）：带内容的 thinking 结束标记会先给内容、再给结束。 */
export function askEventsOf(event: SseEvent): AskEvent[] {
  const text = typeof event.content === 'string' ? event.content : ''
  switch (event.response_type) {
    case 'thinking': {
      const out: AskEvent[] = []
      if (text) out.push({ kind: 'thinking', text })
      if (event.done === true) out.push({ kind: 'thinking-done' })
      return out
    }
    case 'answer':
      return text ? [{ kind: 'answer', text }] : []
    case 'references':
      // 元素必须是对象：后面读 `ref.id`、`ref.knowledge_title` 的地方都默认如此，一个 null 就会让整条回答渲染不出来
      return Array.isArray(event.knowledge_references)
        ? [{ kind: 'references', references: event.knowledge_references.filter((ref): ref is Reference => isRecord(ref)).slice(0, LIMITS.references) }]
        : []
    default:
      return []
  }
}

/** 把一路事件合成完整回答：回答片段拼起来并去掉首尾空白，引用以最后一次为准，思考不算回答。 */
export function foldAnswer(events: Iterable<AskEvent>): { answer: string; references: Reference[] } {
  let answer = ''
  let references: Reference[] = []
  for (const event of events) {
    if (event.kind === 'answer') answer += event.text
    if (event.kind === 'references') references = event.references
  }
  return { answer: answer.trim(), references }
}
